'use client'

import type { PostHog } from 'posthog-js'
import type { EventName } from './analytics-events'

const enabled = () => typeof window !== 'undefined' && !!process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN
type AnalyticsClient = Pick<PostHog, 'capture' | 'identify' | 'reset'>
let client: AnalyticsClient | undefined
let starting: Promise<void> | undefined
const queued: ((client: AnalyticsClient) => void)[] = []

function withAnalytics(call: (client: AnalyticsClient) => void) {
  if (client) call(client)
  else if (enabled()) queued.push(call)
}

/** Embedded IDE surfaces use their host's deferred client and identity. */
export function connectAnalytics(analytics: AnalyticsClient) {
  client = analytics
  for (const call of queued) call(analytics)
  queued.length = 0
}

/** Keep the SDK off the hydration path; preserve identity changes and captures in order. */
export function startAnalytics(): Promise<void> {
  if (!enabled()) return Promise.resolve()
  starting ??= import('posthog-js').then(({ default: posthog }) => {
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN!, {
      ...(host === undefined ? {} : { api_host: host }),
      defaults: '2026-05-30',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
    })
    connectAnalytics(posthog)
  })
  return starting
}

/** Capture time belongs to the interaction, not the deferred SDK initialization. */
export function track(event: EventName, properties?: Record<string, unknown>) {
  const timestamp = new Date()
  withAnalytics((analytics) => analytics.capture(event, properties, { timestamp }))
}

export function identifyStudent(subjectId: string, email: string) {
  withAnalytics((analytics) => analytics.identify(subjectId, { email }))
}

export function resetAnalytics() {
  withAnalytics((analytics) => analytics.reset())
}
